import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormLabel,
  Input,
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import ReactSelect from 'react-select'

import { IBlogPostFields, BlogPostService } from '../../../services/BlogPost/BlogPostService'
import { BlogPostComplete, KnowledgeArea } from '../../../services/BlogPost/types'

interface IProps {
  isOpen: boolean
  onClose: () => void
  blogPost?: BlogPostComplete
}

const baseForm: IBlogPostFields = {
  title: '',
  header: '',
  body: '',
  popular: false,
  knowledge_areas: []
}

const baseFormErrors = {
  title: '',
  header: '',
  body: '',
  popular: '',
  knowledge_areas: ''
}

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ script: 'sub' }, { script: 'super' }],
    [{ indent: '-1' }, { indent: '+1' }],
    [{ direction: 'rtl' }],
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ color: [] }, { background: [] }],
    [{ font: [] }],
    [{ align: [] }],
    ['link', 'image', 'video'],
    ['clean']
  ]
}

const quillFormats = [
  'header',
  'font',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'blockquote',
  'list',
  'bullet',
  'indent',
  'link',
  'image',
  'video',
  'align',
  'color',
  'background',
  'script',
  'code-block',
  'direction'
]

export function BlogPostFormModal({ isOpen, onClose, blogPost }: IProps) {
  const service = new BlogPostService()
  const [saving, setSaving] = useState(false)
  const toast = useToast()
  const [form, setForm] = useState<IBlogPostFields>()
  const [knowledgeAreas, setKnowledgeAreas] = useState<KnowledgeArea[]>([])
  const [formErrors, setFormErrors] = useState(baseFormErrors)

  useEffect(() => {
    if (isOpen) {
      setSaving(false)
      setFormErrors(baseFormErrors)

      service.getKnowledgeAreas().then(setKnowledgeAreas)

      if (blogPost?.id) {
        getPostData(blogPost.id)
      } else {
        setForm(baseForm)
      }
    }
  }, [isOpen, blogPost])

  const getPostData = async (id: number) => {
    service.get(id).then((response) => {
      setForm({
        title: response.title,
        header: response.header,
        body: response.body,
        popular: response.popular,
        knowledge_areas: response.knowledge_areas.map((ka) => ka.id)
      })
    })
  }

  const handleSave = async () => {
    setSaving(true)

    const saveFunction = blogPost ? service.update(blogPost.id, form!) : service.create(form!)

    saveFunction
      .then(() => {
        toast({
          title: 'Post salvo com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true
        })
        onClose()
      })
      .catch((response) => {
        if (response.response?.status === 422) {
          setFormErrors(response.response.data.errors)
          return
        }

        toast({
          title: blogPost ? 'Erro ao editar post' : 'Erro ao adicionar post',
          status: 'error',
          duration: 3000,
          isClosable: true
        })
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return form ? (
    <ChakraModal isOpen={isOpen} onClose={onClose} size="6xl" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{blogPost ? 'Editar' : 'Cadastrar'} Post do Blog</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Flex direction="column" gap={4}>
            <Flex direction="row" gap={3}>
              <Box w="80%">
                <FormLabel htmlFor="title">Título *</FormLabel>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  required
                  id="title"
                  disabled={saving}
                  maxLength={255}
                />
                {formErrors.title && (
                  <Text color="red.500" fontSize="sm">
                    {formErrors.title}
                  </Text>
                )}
              </Box>

              <Box w="20%" display="flex" alignItems="center" justifyContent="center">
                <Checkbox
                  isChecked={form.popular}
                  onChange={(e) => setForm({ ...form, popular: e.target.checked })}
                  disabled={saving}
                >
                  Popular
                </Checkbox>
              </Box>
            </Flex>

            <Box>
              <FormLabel htmlFor="header">Cabeçalho *</FormLabel>
              <Input
                value={form.header}
                onChange={(e) => setForm({ ...form, header: e.target.value })}
                required
                id="header"
                disabled={saving}
                maxLength={255}
              />
              {formErrors.header && (
                <Text color="red.500" fontSize="sm">
                  {formErrors.header}
                </Text>
              )}
            </Box>

            <Box>
              <FormLabel htmlFor="knowledge_areas">Áreas de Conhecimento *</FormLabel>
              <ReactSelect
                id="knowledge_areas"
                value={form.knowledge_areas
                  .map((areaId) => {
                    const area = knowledgeAreas.find((ka) => ka.id === areaId)
                    return area ? { label: area.name, value: area.id } : null
                  })
                  .filter(Boolean)}
                disabled={saving}
                isMulti
                closeMenuOnSelect={false}
                options={knowledgeAreas.map((area) => ({
                  label: area.name,
                  value: area.id
                }))}
                onChange={(selectedOptions: Array<{ value: number; label: string }>) => {
                  const selectedValues = selectedOptions.map((option) => option.value)
                  setForm({ ...form, knowledge_areas: selectedValues })
                }}
                placeholder="Selecione as áreas de conhecimento"
              />
              {formErrors.knowledge_areas && (
                <Text color="red.500" fontSize="sm">
                  {formErrors.knowledge_areas}
                </Text>
              )}
            </Box>

            <Box>
              <FormLabel htmlFor="body">Conteúdo do Post *</FormLabel>
              <Box border="1px solid" borderColor="gray.200" borderRadius="md">
                <ReactQuill
                  theme="snow"
                  value={form.body}
                  onChange={(content) => setForm({ ...form, body: content })}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Escreva o conteúdo do post aqui..."
                  style={{
                    height: '300px',
                    marginBottom: '42px' // Space for toolbar
                  }}
                />
              </Box>
              {formErrors.body && (
                <Text color="red.500" fontSize="sm">
                  {formErrors.body}
                </Text>
              )}
            </Box>
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="magenta" mr={3} onClick={handleSave} type="submit" disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
          <Button variant="ghost" onClick={onClose} type="button">
            Cancelar
          </Button>
        </ModalFooter>
      </ModalContent>
    </ChakraModal>
  ) : (
    <></>
  )
}
